import React from 'react';
import { endReasonLabel, historyActor, ignoredRowLabel, sourceLabel } from './advisors';
import StackTd from '../StackTable';
import { stackClasses } from '../stack_table';
import Stamp from './Stamp';
import type { History } from './types';
import { LABEL_CLASS } from '../ui';

const ST = stackClasses();

// "By" sits under Source and "End reason" under Ended, so seven fields fit five columns.
const HISTORY_COLS = [
  { label: 'Advisor', width: 'w-[22%]' },
  { label: 'Source', width: 'w-[26%]' },
  { label: 'Assigned', width: 'w-[16%]' },
  { label: 'Expires', width: 'w-[16%]' },
  { label: 'Ended', width: 'w-[20%]' }
];
const IGNORED_HISTORY_COLS = [
  { label: 'When', width: 'w-[22%]' },
  { label: 'Tagged by', width: 'w-[28%]' },
  { label: 'Why nobody was assigned', width: '' }
];
const HEAD = `px-3.5 py-2 text-left ${LABEL_CLASS}`;
const CELL = 'px-3.5 py-2 text-left text-[13px] font-normal align-top';
const SUB = 'block text-[11.5px] text-content-muted';
const ROW = `border-t border-line-subtle text-[12.5px] ${ST.row}`;

function Head({ cols }: { cols: { label: string; width: string }[] }) {
  return (
    <thead className={`border-b border-line-subtle bg-surface-sunken ${ST.head}`}>
      <tr>
        {cols.map((c) => (
          <th key={c.label} className={`${HEAD} ${c.width}`}>
            {c.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

// One student's advisor rows plus the tag notes that assigned nobody; shared by the Advisors tab
// and the student profile popup.
function AdvisorHistoryTables({ data }: { data: History }) {
  const rows = data.rows || [];
  const ignored = data.ignored_tags || [];
  return (
    <>
      <table className={`mt-2 ${ST.table}`} aria-label="Advisor history">
        <Head cols={HISTORY_COLS} />
        <tbody className={ST.body}>
          {rows.length === 0 ? (
            <tr className={ST.row}>
              <StackTd
                colSpan={HISTORY_COLS.length}
                className="px-3 py-4 text-center text-content-muted"
              >
                No advisor history.
              </StackTd>
            </tr>
          ) : (
            rows.map((h) => {
              const actor = historyActor(h);
              const reason = h.ended_at ? endReasonLabel(h.end_reason) : '';
              return (
                <tr key={h.row_id} className={ROW}>
                  <StackTd className={`${CELL} truncate font-semibold`} title={h.advisor_name}>
                    {h.advisor_name}
                  </StackTd>
                  <StackTd label="Source" className={CELL}>
                    <span className="block">{sourceLabel(h.source)}</span>
                    {actor !== '—' ? <span className={SUB}>{actor}</span> : null}
                  </StackTd>
                  <StackTd label="Assigned" className={CELL}>
                    <Stamp iso={h.assigned_at} />
                  </StackTd>
                  <StackTd label="Expires" className={CELL}>
                    <Stamp iso={h.expires_at} />
                  </StackTd>
                  <StackTd label="Ended" className={CELL}>
                    <Stamp iso={h.ended_at} />
                    {reason ? <span className={SUB}>{reason}</span> : null}
                  </StackTd>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {ignored.length > 0 ? (
        <div className="mt-4">
          <h4 className={`m-0 ${LABEL_CLASS}`}>Tags that did not assign</h4>
          <table className={`mt-2 ${ST.table}`} aria-label="Tags that did not assign">
            <Head cols={IGNORED_HISTORY_COLS} />
            <tbody className={ST.body}>
              {ignored.map((t) => (
                <tr key={t.id} className={ROW}>
                  <StackTd label="When" className={CELL}>
                    <Stamp iso={t.note_created_date} />
                  </StackTd>
                  <StackTd
                    label="Tagged by"
                    className={`${CELL} truncate`}
                    title={t.author_name || undefined}
                  >
                    {t.author_name || 'unknown'}
                  </StackTd>
                  <StackTd label="Why nobody was assigned" className={CELL}>
                    {ignoredRowLabel(t.reason, t.detail, t.detail_name)}
                  </StackTd>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

export default AdvisorHistoryTables;
